import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export const AI_CHECK_EVENT = "cj-ai-check";

export default function AiPausedBanner() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const isAdmin = user?.role === "super_admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ai-status");
      setStatus(data);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    window.addEventListener(AI_CHECK_EVENT, load);
    window.addEventListener("focus", load);
    return () => {
      clearInterval(id);
      window.removeEventListener(AI_CHECK_EVENT, load);
      window.removeEventListener("focus", load);
    };
  }, [load]);

  const recheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/ai-status/recheck");
      setStatus(data);
      if (data.paused) toast.error("Still out of balance. Top up, then check again.");
      else toast.success("AI is back on for the whole team.");
    } catch {
      toast.error("Couldn't check right now. Try again in a minute.");
    } finally {
      setChecking(false);
    }
  };

  if (!status?.paused) return null;

  return (
    <div data-testid="ai-paused-banner" role="alert" className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
        <AlertTriangle size={18} className="text-amber-600 shrink-0" />
        <p data-testid="ai-paused-banner-message" className="flex-1 min-w-0 text-sm font-semibold text-amber-900">
          {status.message}
        </p>
        {isAdmin && (
          <div className="flex items-center gap-3 text-xs font-bold">
            <span data-testid="ai-paused-admin-hint" className="text-amber-800">
              Profile → Manage plan → Universal Key → Add Balance
            </span>
            <button
              onClick={recheck}
              disabled={checking}
              data-testid="ai-paused-recheck-btn"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-60"
            >
              <RefreshCw size={13} className={checking ? "animate-spin" : ""} />
              I've topped up, check again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
